import { useCallback } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/icons';
import { AsyncPanel, Panel } from '@/components/platform/AsyncPanel';
import { useEnumLabel } from '@/components/platform/useLabel';
import { platformApi } from '@/api/platform';
import { useAsyncData, type AsyncState } from '@/hooks';
import type { GroupAnalytics, InstitutionGroup, InstitutionGroupMember } from '@/types/platform';
import { formatDateTime } from '@/utils/format';
import { GroupMembersPanel } from './GroupMembersPanel';
import { GroupAdministratorsPanel } from './GroupAdministratorsPanel';
import { GroupProductsPanel } from './GroupProductsPanel';
import { GroupFinancePanel, GroupAnalyticsPanel } from './GroupInsightsPanel';
import { GroupSettingsPanel } from './GroupSettingsPanel';
import styles from './platform-pages.module.css';

/**
 * F2 — one institution group, and everything that belongs to it.
 *
 * SEVEN TABS, THREE QUESTIONS. Overview answers "what is this group"; the
 * institutions, administrators and products tabs answer "what does it hold and
 * who runs it"; finance and analytics answer "how is it doing". The tabs exist
 * because one of these questions can fail without the others being wrong: the
 * members endpoint and the analytics projection are separate surfaces, and the
 * analytics endpoint is being stood up in parallel with this screen.
 *
 * THE HEADER NEVER BLANKS A WORKING TAB. The group row has no dedicated GET —
 * core serves the list and the partial PATCH — so the identity block is found
 * in the list response, and the analytics projection (groupName, groupCode,
 * groupType) is the fallback when the list is unreachable. A missing list must
 * not take the members panel down with it.
 */
const TABS = [
  { id: 'overview', labelKey: 'platform.groups.tab.overview' },
  { id: 'institutions', labelKey: 'platform.groups.tab.institutions' },
  { id: 'administrators', labelKey: 'platform.groups.tab.administrators' },
  { id: 'products', labelKey: 'platform.groups.tab.products' },
  { id: 'finance', labelKey: 'platform.groups.tab.finance' },
  { id: 'analytics', labelKey: 'platform.groups.tab.analytics' },
  { id: 'settings', labelKey: 'platform.groups.tab.settings' },
] as const;

type GroupTab = (typeof TABS)[number]['id'];

function isTab(value: string | null): value is GroupTab {
  return TABS.some((tab) => tab.id === value);
}

export function GroupDetailPage() {
  const { groupId = '' } = useParams();
  const { t, i18n } = useTranslation();
  const label = useEnumLabel();
  const locale = i18n.language;
  const enabled = groupId.length > 0;

  const [params, setParams] = useSearchParams();
  const tabParam = params.get('tab');
  const tab: GroupTab = isTab(tabParam) ? tabParam : 'overview';

  const setTab = (next: GroupTab) => setParams(next === 'overview' ? {} : { tab: next });

  // The group row itself. The list is the only place core serves it whole; a
  // group is found by id rather than requesting a route that does not exist.
  const groupsFetcher = useCallback(() => platformApi.structure.listGroups(), []);
  const { state: groups, reload: reloadGroups } = useAsyncData<InstitutionGroup[]>(
    'groups',
    groupsFetcher,
    enabled,
  );

  const membersFetcher = useCallback(() => platformApi.structure.groupMembers(groupId), [groupId]);
  const { state: members, reload: reloadMembers } = useAsyncData<InstitutionGroupMember[]>(
    `group-members:${groupId}`,
    membersFetcher,
    enabled,
  );

  const analyticsFetcher = useCallback(() => platformApi.structure.groupAnalytics(groupId), [groupId]);
  const { state: analytics, reload: reloadAnalytics } = useAsyncData<GroupAnalytics>(
    `group-analytics:${groupId}`,
    analyticsFetcher,
    enabled,
  );

  const group = groups.status === 'ready' ? (groups.data.find((entry) => entry.id === groupId) ?? null) : null;
  const groupName = group?.name ?? (analytics.status === 'ready' ? analytics.data.groupName : groupId);
  const groupCode = group?.code ?? (analytics.status === 'ready' ? analytics.data.groupCode : '');
  const groupType = group?.type ?? (analytics.status === 'ready' ? analytics.data.groupType : null);

  return (
    <div className={styles.page}>
      <nav className={styles.breadcrumb} aria-label={t('platform.groups.breadcrumbLabel')}>
        <Link to="/admin/groups">{t('platform.groups.title')}</Link>
        <Icon name="chevronRight" size={13} />
        <span>{groupName}</span>
      </nav>

      <header className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>{groupName}</h1>
          <p className={styles.pageLead}>
            {groupCode ? `${groupCode} · ` : ''}
            {groupType ? label('structure.groupType', groupType) : t('platform.groups.unknownType')}
          </p>
        </div>
        <div className={styles.caseHeaderAside}>
          <span className={group?.isActive === false ? styles.mutedTag : styles.okTag}>
            {t(
              group?.isActive === false ? 'platform.groups.status.INACTIVE' : 'platform.groups.status.ACTIVE',
            )}
          </span>
        </div>
      </header>

      <div className={styles.tabBar} role="tablist" aria-label={t('platform.groups.tabsLabel')}>
        {TABS.map((candidate) => (
          <button
            key={candidate.id}
            type="button"
            role="tab"
            id={`group-tab-${candidate.id}`}
            aria-selected={tab === candidate.id}
            aria-controls="group-results"
            className={`${styles.tab} ${tab === candidate.id ? styles.tabActive : ''}`}
            onClick={() => setTab(candidate.id)}
          >
            {t(candidate.labelKey)}
          </button>
        ))}
      </div>

      <div id="group-results" role="tabpanel" aria-labelledby={`group-tab-${tab}`}>
        {tab === 'overview' && (
          <GroupOverviewPanel
            group={group}
            analyticsState={analytics}
            membersState={members}
            onRetryAnalytics={reloadAnalytics}
            onRetryMembers={reloadMembers}
            locale={locale}
          />
        )}

        {tab === 'institutions' && (
          <GroupMembersPanel groupId={groupId} membersState={members} onRetry={reloadMembers} />
        )}

        {tab === 'administrators' && <GroupAdministratorsPanel groupId={groupId} groupName={groupName} />}

        {tab === 'products' && (
          <GroupProductsPanel groupId={groupId} analyticsState={analytics} onRetry={reloadAnalytics} />
        )}

        {tab === 'finance' && <GroupFinancePanel analyticsState={analytics} onRetry={reloadAnalytics} />}

        {tab === 'analytics' && <GroupAnalyticsPanel analyticsState={analytics} onRetry={reloadAnalytics} />}

        {tab === 'settings' && <GroupSettingsPanel group={group} groupsState={groups} onRetry={reloadGroups} />}
      </div>
    </div>
  );
}

function GroupOverviewPanel({
  group,
  analyticsState,
  membersState,
  onRetryAnalytics,
  onRetryMembers,
  locale,
}: {
  group: InstitutionGroup | null;
  analyticsState: AsyncState<GroupAnalytics>;
  membersState: AsyncState<InstitutionGroupMember[]>;
  onRetryAnalytics: () => void;
  onRetryMembers: () => void;
  locale: string;
}) {
  const { t } = useTranslation();

  return (
    <>
      <Panel title={t('platform.groups.overview.title')} icon="building">
        {group ? (
          <dl className={styles.detailGrid}>
            <dt>{t('platform.groups.fieldName')}</dt>
            <dd>{group.name}</dd>
            <dt>{t('platform.groups.fieldCode')}</dt>
            <dd>{group.code}</dd>
            <dt>{t('platform.groups.fieldType')}</dt>
            <dd>{t(`platform.structure.groupType.${group.type}`, { defaultValue: group.type })}</dd>
            <dt>{t('platform.groups.fieldDescription')}</dt>
            <dd>{group.description || '—'}</dd>
            <dt>{t('platform.groups.fieldCountry')}</dt>
            <dd>{group.countryCode || '—'}</dd>
          </dl>
        ) : (
          <p className={styles.sectionEmpty} role="status">
            {t('platform.groups.overview.unavailable')}
          </p>
        )}
      </Panel>

      {/*
        The counts are a projection, not a second copy of the membership list.
        The overview reads them from analytics and the members tab reads the
        membership rows; the two can disagree while the analytics endpoint is
        settling, and that is shown as-is rather than reconciled away.
      */}
      <Panel title={t('platform.groups.overview.countsTitle')} icon="pulse">
        <AsyncPanel state={analyticsState} onRetry={onRetryAnalytics}>
          {(analytics) => (
            <dl className={styles.detailGrid}>
              <dt>{t('platform.groups.overview.institutions')}</dt>
              <dd>{analytics.institutionCount}</dd>
              <dt>{t('platform.groups.overview.active')}</dt>
              <dd>{analytics.activeInstitutionCount}</dd>
              <dt>{t('platform.groups.overview.campuses')}</dt>
              <dd>{analytics.campusCount}</dd>
              <dt>{t('platform.groups.overview.branches')}</dt>
              <dd>{analytics.branchCount}</dd>
            </dl>
          )}
        </AsyncPanel>
      </Panel>

      {/*
        The head office is a designation with consequences (invoicing follows
        it), so the overview says which member holds it before the institutions
        tab does. The members endpoint is the source of truth for roles.
      */}
      <Panel title={t('platform.groups.overview.headOfficeTitle')} icon="building">
        <AsyncPanel state={membersState} onRetry={onRetryMembers}>
          {(data) => {
            const head = data.find((member) => member.role === 'HEAD_OFFICE');
            if (!head) {
              return (
                <p className={styles.sectionEmpty} role="status">
                  {t('platform.groups.overview.noHeadOffice')}
                </p>
              );
            }
            return (
              <ul className={styles.rowList}>
                <li key={head.id} className={styles.row}>
                  <div className={styles.rowMain}>
                    <p className={styles.rowTitle}>{head.institutionName ?? head.institutionId}</p>
                    <p className={styles.rowMetaLine}>
                      {t(`platform.structure.membership.${head.role}`, { defaultValue: head.role })}
                    </p>
                  </div>
                </li>
              </ul>
            );
          }}
        </AsyncPanel>
      </Panel>

      <Panel title={t('platform.groups.overview.membersTitle')} icon="institution">
        <AsyncPanel
          state={membersState}
          onRetry={onRetryMembers}
          isEmpty={(data) => data.length === 0}
          emptyTitle={t('platform.groups.members.emptyTitle')}
          emptyBody={t('platform.groups.members.emptyBody')}
        >
          {(data) => (
            <ul className={styles.rowList}>
              {data.map((member) => (
                <li key={member.id} className={styles.row}>
                  <div className={styles.rowMain}>
                    <Link
                      className={styles.rowTitleLink}
                      to={`/admin/institutions/${encodeURIComponent(member.institutionId)}`}
                    >
                      {member.institutionName ?? member.institutionId}
                    </Link>
                    <p className={styles.rowMetaLine}>
                      {t(`platform.structure.membership.${member.role}`, { defaultValue: member.role })}
                      {member.joinedAt
                        ? ` · ${t('platform.groups.members.joined', { when: formatDateTime(member.joinedAt, locale) })}`
                        : ''}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </AsyncPanel>
      </Panel>
    </>
  );
}
